import { FC, useEffect, useRef } from 'react';

import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import { ReactComponent as ChevronRightIcon } from '@/assets/icons/search-chevron-right.svg';
import StatusPill from '@/shared/ui/StatusPill';

import { SearchCategory, SearchHitResponse } from '../model/types';
import HighlightedText from './HighlightedText';
import { getSearchCategoryVisual } from './categoryVisuals';

interface Props {
  hit: SearchHitResponse;
  category: SearchCategory;
  query: string;
  isActive: boolean;
  onOpen: () => void;
  onActivate: () => void;
}

const SearchResultRow: FC<Props> = ({ hit, category, query, isActive, onOpen, onActivate }) => {
  const rowRef = useRef<HTMLButtonElement | null>(null);
  const { Icon, thumbColor, thumbBackground } = getSearchCategoryVisual(category);
  const status = hit.attributes?.status;
  const orderNumber = hit.attributes?.orderNumber;
  const primaryText = hit.title;
  const secondaryText = hit.subtitle;
  const meta = orderNumber && !primaryText.includes(orderNumber) ? orderNumber : undefined;

  useEffect(() => {
    if (isActive) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isActive]);

  return (
    <ButtonBase
      ref={rowRef}
      onClick={onOpen}
      onMouseMove={isActive ? undefined : onActivate}
      data-testid="search-hit-row"
      data-hit-id={hit.id}
      data-active={isActive || undefined}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        gap: '0.75rem',
        padding: '0.5rem 0.625rem',
        borderRadius: '0.5rem',
        backgroundColor: isActive ? 'grey.50' : 'transparent',
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: '2rem',
          height: '2rem',
          borderRadius: '0.5rem',
          flexShrink: 0,
          backgroundColor: thumbBackground,
          '& svg': { width: '1.25rem', height: '1.25rem', color: thumbColor },
        }}
      >
        <Icon />
      </Stack>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" gap="0.5rem" minWidth={0}>
          <Typography
            noWrap
            sx={{ fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.900' }}
          >
            <HighlightedText text={primaryText} query={query} />
          </Typography>
          {meta && (
            <Typography
              noWrap
              sx={{
                fontSize: '0.625rem',
                fontWeight: 350,
                lineHeight: 1.25,
                color: 'grey.900',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <HighlightedText text={`#${meta}`} query={query} />
            </Typography>
          )}
        </Stack>
        {secondaryText && (
          <Typography
            noWrap
            display="block"
            sx={{ fontSize: '0.625rem', fontWeight: 350, lineHeight: 1.25, color: 'grey.900' }}
          >
            <HighlightedText text={secondaryText} query={query} />
          </Typography>
        )}
      </Box>

      {status && <StatusPill status={status} />}

      <Box
        component={ChevronRightIcon}
        sx={{
          width: '1.25rem',
          height: '1.25rem',
          opacity: isActive ? 1 : 0,
          flexShrink: 0,
        }}
      />
    </ButtonBase>
  );
};

export default SearchResultRow;
